import React, { useState, useRef, useMemo } from 'react';
import { Upload, X, Image as ImageIcon, Type, ArrowLeft, ArrowRight, Sparkles, Plus, Minus, Loader2 } from 'lucide-react';
import { uploadDataUriToBackend, isAlreadyPersisted } from '../../services/backendService';

// Nine canonical positions on the canvas (percentages).
const POSITIONS = [
    { id: 'tl', label: 'Top Left',     x: 4,  y: 4  },
    { id: 'tc', label: 'Top Center',   x: 50, y: 4  },
    { id: 'tr', label: 'Top Right',    x: 96, y: 4  },
    { id: 'ml', label: 'Middle Left',  x: 4,  y: 50 },
    { id: 'mc', label: 'Center',       x: 50, y: 50 },
    { id: 'mr', label: 'Middle Right', x: 96, y: 50 },
    { id: 'bl', label: 'Bottom Left',  x: 4,  y: 96 },
    { id: 'bc', label: 'Bottom Center',x: 50, y: 96 },
    { id: 'br', label: 'Bottom Right', x: 96, y: 96 },
];

const PositionGrid = ({ value, onChange, testidPrefix }) => (
    <div className="grid grid-cols-3 gap-1.5 w-32" data-testid={`${testidPrefix}-position-grid`}>
        {POSITIONS.map(p => {
            const active = Math.round(value.x) === p.x && Math.round(value.y) === p.y;
            return (
                <button
                    key={p.id}
                    type="button"
                    onClick={() => onChange({ x: p.x, y: p.y })}
                    title={p.label}
                    data-testid={`${testidPrefix}-pos-${p.id}`}
                    className={`aspect-square rounded-md transition-all flex items-center justify-center ${
                        active
                            ? 'bg-violet-600 ring-1 ring-violet-400 shadow-lg shadow-violet-500/30'
                            : 'bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/25'
                    }`}
                >
                    <div className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-white' : 'bg-zinc-500'}`} />
                </button>
            );
        })}
    </div>
);

// Reusable +/- numeric stepper. Used for both Size and Opacity.
const Stepper = ({ value, onChange, min, max, step = 1, suffix = '%', testid }) => {
    const dec = () => onChange(Math.max(min, +(value - step).toFixed(2)));
    const inc = () => onChange(Math.min(max, +(value + step).toFixed(2)));
    return (
        <div className="flex items-center gap-2" data-testid={testid}>
            <button
                type="button"
                onClick={dec}
                disabled={value <= min}
                className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/25 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-zinc-300 transition-all"
                aria-label="Decrease"
                data-testid={`${testid}-dec`}
            >
                <Minus className="w-3.5 h-3.5" />
            </button>
            <div className="min-w-[60px] text-center text-sm font-semibold text-white tabular-nums" data-testid={`${testid}-value`}>
                {value}{suffix}
            </div>
            <button
                type="button"
                onClick={inc}
                disabled={value >= max}
                className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/25 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-zinc-300 transition-all"
                aria-label="Increase"
                data-testid={`${testid}-inc`}
            >
                <Plus className="w-3.5 h-3.5" />
            </button>
        </div>
    );
};

// Mock preview canvas — respects aspect ratio of the project so the preview
// looks like the real video frame.
const MockPreview = ({ aspectRatio, children, label, testid }) => {
    const ratio = useMemo(() => {
        const m = (aspectRatio || '16:9').split(':');
        const w = parseFloat(m[0]) || 16;
        const h = parseFloat(m[1]) || 9;
        return w / h;
    }, [aspectRatio]);
    // Cap height for portrait/square so it doesn't dominate the page.
    const isLandscape = ratio >= 1;
    const style = isLandscape
        ? { width: '100%', maxWidth: 360, aspectRatio: `${ratio}` }
        : { width: 200, aspectRatio: `${ratio}` };
    return (
        <div className="flex flex-col items-center gap-2">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</div>
            <div
                className="relative bg-gradient-to-br from-zinc-800 via-zinc-900 to-black rounded-lg overflow-hidden border border-white/10"
                style={style}
                data-testid={testid}
            >
                {/* fake film grain / hint */}
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(139,92,246,0.08),transparent_60%)]" />
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-zinc-700 text-[10px] uppercase tracking-widest">Your video</div>
                </div>
                {children}
            </div>
        </div>
    );
};

// Renders an overlay marker at (x%, y%) on the mock preview at the given
// width (relative to the canvas) and opacity (0–1).
const OverlayMarker = ({ x, y, width, opacity = 1, children, kind }) => {
    return (
        <div
            className="absolute pointer-events-none"
            style={{
                left: `${x}%`,
                top: `${y}%`,
                width: `${width}%`,
                aspectRatio: kind === 'logo' ? '1' : '16 / 9',
                transform: 'translate(-50%, -50%)',
                opacity,
            }}
        >
            <div className={`w-full h-full rounded-md flex items-center justify-center text-center px-2 ${
                kind === 'logo'
                    ? 'bg-violet-500/30 border-2 border-dashed border-violet-300'
                    : 'bg-emerald-500/20 border-2 border-dashed border-emerald-300'
            }`}>
                {children}
            </div>
        </div>
    );
};

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
});

export const Branding = ({ initialData, onSubmit, onBack, onAutoSave }) => {
    const aspectRatio = initialData.aspectRatio || '16:9';
    const existing = initialData.branding || {};

    // Default logo size depends on the aspect ratio: 16:9 frames are wider,
    // so the same percentage produces a much larger logo in absolute terms.
    // Per product spec, default to 4% width on 16:9 and 15% on portrait/square.
    const defaultLogoSize = aspectRatio === '16:9' ? 4 : 15;

    // ---------- Logo state ----------
    const [logoEnabled, setLogoEnabled] = useState(Boolean(existing.logo?.enabled));
    const [logoUrl, setLogoUrl] = useState(existing.logo?.src || '');
    const [logoName, setLogoName] = useState(existing.logo?.name || '');
    const [logoPos, setLogoPos] = useState(existing.logo?.position || { x: 4, y: 4 });
    const [logoSize, setLogoSize] = useState(existing.logo?.width ?? defaultLogoSize);    // % of canvas width
    const [logoOpacity, setLogoOpacity] = useState(existing.logo?.opacity ?? 1);
    const logoInputRef = useRef(null);

    // ---------- Outro state ----------
    const [outroEnabled, setOutroEnabled] = useState(Boolean(existing.outro?.enabled));
    const [outroType, setOutroType] = useState(existing.outro?.type || 'image'); // 'image' | 'text'
    const [outroImageUrl, setOutroImageUrl] = useState(existing.outro?.imageSrc || '');
    const [outroImageName, setOutroImageName] = useState(existing.outro?.imageName || '');
    const [outroText, setOutroText] = useState(existing.outro?.text || '');
    const [outroPos, setOutroPos] = useState(existing.outro?.position || { x: 50, y: 50 });
    const [outroSize, setOutroSize] = useState(existing.outro?.width ?? 60);   // % of canvas width
    const [outroOpacity, setOutroOpacity] = useState(existing.outro?.opacity ?? 1);
    const outroInputRef = useRef(null);

    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) { setError('Logo must be an image file.'); return; }
        if (file.size > 5 * 1024 * 1024) { setError('Logo must be under 5 MB.'); return; }
        try {
            const url = await fileToDataUrl(file);
            setLogoUrl(url);
            setLogoName(file.name);
            setLogoEnabled(true);
            setError(null);
        } catch {
            setError('Could not read the logo file.');
        }
        if (e.target) e.target.value = '';
    };

    const handleOutroImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) { setError('Outro image must be an image file.'); return; }
        if (file.size > 5 * 1024 * 1024) { setError('Outro image must be under 5 MB.'); return; }
        try {
            const url = await fileToDataUrl(file);
            setOutroImageUrl(url);
            setOutroImageName(file.name);
            setError(null);
        } catch {
            setError('Could not read the outro image.');
        }
        if (e.target) e.target.value = '';
    };

    const removeLogo = () => { setLogoUrl(''); setLogoName(''); setLogoEnabled(false); };
    const removeOutroImage = () => { setOutroImageUrl(''); setOutroImageName(''); };

    const buildBrandingPayload = () => ({
        logo: logoEnabled && logoUrl
            ? {
                enabled: true,
                src: logoUrl,
                name: logoName || 'Logo',
                position: logoPos,
                width: logoSize,
                height: logoSize,
                opacity: logoOpacity,
            }
            : { enabled: false },
        outro: outroEnabled
            ? (outroType === 'image' && outroImageUrl
                ? {
                    enabled: true,
                    type: 'image',
                    imageSrc: outroImageUrl,
                    imageName: outroImageName || 'Outro image',
                    position: outroPos,
                    duration: 3,
                    width: outroSize,
                    opacity: outroOpacity,
                }
                : outroType === 'text' && outroText.trim()
                    ? {
                        enabled: true,
                        type: 'text',
                        text: outroText.trim(),
                        position: outroPos,
                        duration: 3,
                        width: outroSize,
                        opacity: outroOpacity,
                    }
                    : { enabled: false })
            : { enabled: false },
    });

    // Upload a data: URL to GCS (returns the GCS URL). If already a real URL,
    // returns it as-is. Required because the cloud renderer can't fetch
    // data: URLs server-side.
    const persistImage = async (url) => {
        if (!url) return url;
        if (isAlreadyPersisted(url)) return url;
        if (!url.startsWith('data:')) return url;
        try {
            const userEmail = sessionStorage.getItem('userEmail');
            const gcsUrl = await uploadDataUriToBackend(url, userEmail);
            return gcsUrl || url;
        } catch (e) {
            console.warn('[Branding] persistImage failed:', e?.message || e);
            return url;
        }
    };

    const handleContinue = async (e) => {
        e?.preventDefault();
        if (isSubmitting) return;
        // Light validation: if a section is enabled but missing content, warn.
        if (logoEnabled && !logoUrl) { setError('Upload a logo image or turn the logo off.'); return; }
        if (outroEnabled) {
            if (outroType === 'image' && !outroImageUrl) { setError('Upload an outro image or switch to text.'); return; }
            if (outroType === 'text' && !outroText.trim()) { setError('Type your outro text or switch to image.'); return; }
        }
        setIsSubmitting(true);
        try {
            // Persist any data: URLs to GCS BEFORE we hand off so the timeline
            // and the cloud render get a real URL the backend can fetch.
            let persistedLogoUrl = logoUrl;
            let persistedOutroImageUrl = outroImageUrl;
            if (logoEnabled && logoUrl?.startsWith('data:')) {
                persistedLogoUrl = await persistImage(logoUrl);
                setLogoUrl(persistedLogoUrl);
            }
            if (outroEnabled && outroType === 'image' && outroImageUrl?.startsWith('data:')) {
                persistedOutroImageUrl = await persistImage(outroImageUrl);
                setOutroImageUrl(persistedOutroImageUrl);
            }

            // Inline payload build using the freshly-persisted URLs (don't
            // race with React state).
            const branding = {
                logo: logoEnabled && persistedLogoUrl
                    ? {
                        enabled: true,
                        src: persistedLogoUrl,
                        name: logoName || 'Logo',
                        position: logoPos,
                        width: logoSize,
                        height: logoSize,
                        opacity: logoOpacity,
                    }
                    : { enabled: false },
                outro: outroEnabled
                    ? (outroType === 'image' && persistedOutroImageUrl
                        ? {
                            enabled: true,
                            type: 'image',
                            imageSrc: persistedOutroImageUrl,
                            imageName: outroImageName || 'Outro image',
                            position: outroPos,
                            duration: 3,
                            width: outroSize,
                            opacity: outroOpacity,
                        }
                        : outroType === 'text' && outroText.trim()
                            ? {
                                enabled: true,
                                type: 'text',
                                text: outroText.trim(),
                                position: outroPos,
                                duration: 3,
                                width: outroSize,
                                opacity: outroOpacity,
                            }
                            : { enabled: false })
                    : { enabled: false },
            };
            if (onAutoSave) {
                const override = { ...initialData, branding, lastModified: Date.now() };
                try { await onAutoSave({ reason: 'branding-step', projectOverride: override }); } catch { /* noop */ }
            }
            onSubmit(branding);
        } catch (err) {
            setError('Could not save branding assets. Please try again. ' + (err?.message || ''));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="h-full overflow-y-auto" data-testid="branding-page-scroll">
            <div className="max-w-5xl mx-auto py-8 px-4">
            <div className="bg-zinc-950/80 border border-white/10 rounded-2xl p-6 sm:p-8 shadow-2xl">
                <div className="flex items-start gap-4 mb-8">
                    <div className="w-12 h-12 rounded-xl bg-violet-500/15 border border-violet-500/30 flex items-center justify-center shrink-0">
                        <Sparkles className="w-6 h-6 text-violet-300" />
                    </div>
                    <div className="flex-1">
                        <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">Add your branding</h2>
                        <p className="text-sm text-zinc-300 mt-2 leading-relaxed">
                            Drop your logo on every frame and add a short outro card at the end of the video — like a polished sign-off.
                            Both are <span className="text-white font-semibold">optional</span>, and we&apos;ll add them to your timeline automatically.
                        </p>
                    </div>
                    {/* Phase 4 (Feb 2026): Skip branding shortcut. Disables
                        logo + outro and jumps straight to the next step so
                        users who don't care about branding get a one-click
                        bypass without ticking off every toggle by hand. */}
                    <button
                        type="button"
                        onClick={() => {
                            setLogoEnabled(false);
                            setOutroEnabled(false);
                            // Use a microtask delay so the state updates
                            // commit before handleContinue reads them.
                            setTimeout(() => handleContinue(), 0);
                        }}
                        disabled={isSubmitting}
                        className="hidden sm:inline-flex flex-shrink-0 items-center gap-1.5 px-3 py-2 rounded-xl border border-white/15 text-xs text-zinc-300 hover:bg-white/5 transition-all disabled:opacity-40"
                        data-testid="branding-skip-btn"
                        title="Skip branding and continue without a logo or outro"
                    >
                        Skip branding
                        <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                </div>

                {/* ============================ LOGO ============================ */}
                <section className="mb-10" data-testid="branding-logo-section">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-7 h-7 rounded-full bg-violet-500/20 border border-violet-500/40 flex items-center justify-center text-xs font-bold text-violet-300">1</div>
                        <div className="text-base sm:text-lg font-semibold text-white">Your logo</div>
                    </div>
                    <p className="text-sm text-zinc-300 leading-relaxed mb-4 ml-9">
                        Your logo will appear on every frame of the video — perfect for brand recognition.
                        Skip this step if you'd rather have a clean, logo-free video.
                    </p>

                    <div className="ml-9 mb-4">
                        <label className="inline-flex items-center gap-3 cursor-pointer select-none px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 hover:border-white/25 transition-all" data-testid="branding-logo-toggle">
                            <input
                                type="checkbox"
                                checked={logoEnabled}
                                onChange={(e) => setLogoEnabled(e.target.checked)}
                                className="w-4 h-4 accent-violet-500"
                            />
                            <span className="text-sm text-zinc-200 font-medium">Yes, add my logo to the video</span>
                        </label>
                    </div>

                    {logoEnabled && (
                        <div className="grid md:grid-cols-2 gap-6 items-start ml-9">
                            {/* Left: upload + grid */}
                            <div className="space-y-5">
                                <div>
                                    <div className="text-sm font-semibold text-zinc-200 mb-1">Upload your logo</div>
                                    <p className="text-xs text-zinc-400 mb-2">A transparent PNG works best so it sits cleanly on top of your video.</p>
                                    {logoUrl ? (
                                        <div className="relative bg-white/5 border border-white/10 rounded-xl p-4 flex items-center gap-3" data-testid="branding-logo-preview-card">
                                            <div className="w-14 h-14 rounded-lg bg-zinc-800 border border-white/10 flex items-center justify-center overflow-hidden">
                                                <img src={logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm text-white truncate">{logoName}</div>
                                                <button type="button" onClick={() => logoInputRef.current?.click()} className="text-[11px] text-violet-300 hover:text-violet-200">Replace</button>
                                            </div>
                                            <button type="button" onClick={removeLogo} className="text-zinc-500 hover:text-rose-400" aria-label="Remove logo" data-testid="branding-logo-remove-btn">
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => logoInputRef.current?.click()}
                                            className="w-full bg-white/5 border-2 border-dashed border-white/15 hover:border-violet-500/50 hover:bg-white/[0.07] rounded-xl py-8 px-4 flex flex-col items-center justify-center gap-2 transition-all"
                                            data-testid="branding-logo-upload-btn"
                                        >
                                            <Upload className="w-5 h-5 text-zinc-400" />
                                            <span className="text-sm text-zinc-300">Click to upload your logo</span>
                                            <span className="text-[11px] text-zinc-500">PNG / JPG / SVG · up to 5 MB</span>
                                        </button>
                                    )}
                                    <input ref={logoInputRef} type="file" accept="image/*" hidden onChange={handleLogoUpload} data-testid="branding-logo-file-input" />
                                </div>

                                <div>
                                    <div className="text-sm font-semibold text-zinc-200 mb-1">Where should it sit?</div>
                                    <p className="text-xs text-zinc-400 mb-2">Tap a corner or edge. Most brands pick a top corner so the logo doesn't cover faces or action.</p>
                                    <PositionGrid value={logoPos} onChange={setLogoPos} testidPrefix="branding-logo" />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <div className="text-sm font-semibold text-zinc-200 mb-1">Size</div>
                                        <p className="text-xs text-zinc-400 mb-2">How wide on screen.</p>
                                        <Stepper value={logoSize} onChange={setLogoSize} min={5} max={50} step={1} suffix="%" testid="branding-logo-size" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-semibold text-zinc-200 mb-1">Opacity</div>
                                        <p className="text-xs text-zinc-400 mb-2">100% = solid. Lower = subtle watermark.</p>
                                        <Stepper value={Math.round(logoOpacity * 100)} onChange={(v) => setLogoOpacity(v / 100)} min={10} max={100} step={5} suffix="%" testid="branding-logo-opacity" />
                                    </div>
                                </div>

                                <div className="px-3 py-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs leading-relaxed" data-testid="branding-logo-tip">
                                    <span className="font-bold">Tip:</span> keep your logo small (around 4–8% on landscape, up to 15% on portrait) so it doesn't distract from the action. We've pre-set this for you.
                                </div>
                            </div>

                            {/* Right: mock preview */}
                            <div className="flex justify-center md:sticky md:top-4">
                                <MockPreview aspectRatio={aspectRatio} label="Logo placement preview" testid="branding-logo-mock">
                                    {logoUrl ? (
                                        <OverlayMarker x={logoPos.x} y={logoPos.y} width={logoSize} opacity={logoOpacity} kind="logo">
                                            <img src={logoUrl} alt="" className="max-w-full max-h-full object-contain" />
                                        </OverlayMarker>
                                    ) : (
                                        <OverlayMarker x={logoPos.x} y={logoPos.y} width={logoSize} opacity={logoOpacity} kind="logo">
                                            <ImageIcon className="w-5 h-5 text-violet-200" />
                                        </OverlayMarker>
                                    )}
                                </MockPreview>
                            </div>
                        </div>
                    )}
                </section>

                {/* ============================ OUTRO ============================ */}
                <section className="mb-8" data-testid="branding-outro-section">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-7 h-7 rounded-full bg-violet-500/20 border border-violet-500/40 flex items-center justify-center text-xs font-bold text-violet-300">2</div>
                        <div className="text-base sm:text-lg font-semibold text-white">Outro card</div>
                    </div>
                    <p className="text-sm text-zinc-300 leading-relaxed mb-4 ml-9">
                        A short card that plays at the end of the video — like the last frame of a movie. Use it to share your website, a hashtag, or a call to action.
                    </p>

                    <div className="ml-9 mb-4">
                        <label className="inline-flex items-center gap-3 cursor-pointer select-none px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 hover:border-white/25 transition-all" data-testid="branding-outro-toggle">
                            <input
                                type="checkbox"
                                checked={outroEnabled}
                                onChange={(e) => setOutroEnabled(e.target.checked)}
                                className="w-4 h-4 accent-violet-500"
                            />
                            <span className="text-sm text-zinc-200 font-medium">Yes, add an outro to the end</span>
                        </label>
                    </div>

                    {outroEnabled && (
                        <div className="grid md:grid-cols-2 gap-6 items-start ml-9">
                            <div className="space-y-5">
                                {/* Type selector */}
                                <div>
                                    <div className="text-sm font-semibold text-zinc-200 mb-1">What's on your outro?</div>
                                    <p className="text-xs text-zinc-400 mb-2">Pick an image (a final brand frame) or just plain text.</p>
                                    <div className="grid grid-cols-2 gap-2" data-testid="branding-outro-type-selector">
                                    {[
                                        { id: 'image', label: 'Image', icon: ImageIcon },
                                        { id: 'text',  label: 'Text',  icon: Type },
                                    ].map(opt => {
                                        const active = outroType === opt.id;
                                        const Icon = opt.icon;
                                        return (
                                            <button
                                                key={opt.id}
                                                type="button"
                                                onClick={() => setOutroType(opt.id)}
                                                className={`px-3 py-2.5 rounded-xl border text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                                                    active
                                                        ? 'bg-violet-500/15 border-violet-500/50 text-violet-200 ring-1 ring-violet-500/20'
                                                        : 'bg-white/5 border-white/10 text-zinc-400 hover:border-white/25 hover:text-zinc-200'
                                                }`}
                                                data-testid={`branding-outro-type-${opt.id}`}
                                            >
                                                <Icon className="w-4 h-4" />
                                                {opt.label}
                                            </button>
                                        );
                                    })}
                                    </div>
                                </div>

                                {outroType === 'image' && (
                                    <div>
                                        <div className="text-sm font-semibold text-zinc-200 mb-1">Upload outro image</div>
                                        <p className="text-xs text-zinc-400 mb-2">A still frame that closes your video — a logo lockup, hashtag, or call-to-action graphic.</p>
                                        {outroImageUrl ? (
                                            <div className="relative bg-white/5 border border-white/10 rounded-xl p-4 flex items-center gap-3" data-testid="branding-outro-image-preview-card">
                                                <div className="w-14 h-14 rounded-lg bg-zinc-800 border border-white/10 flex items-center justify-center overflow-hidden">
                                                    <img src={outroImageUrl} alt="" className="max-w-full max-h-full object-contain" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm text-white truncate">{outroImageName}</div>
                                                    <button type="button" onClick={() => outroInputRef.current?.click()} className="text-[11px] text-violet-300 hover:text-violet-200">Replace</button>
                                                </div>
                                                <button type="button" onClick={removeOutroImage} className="text-zinc-500 hover:text-rose-400" aria-label="Remove outro image" data-testid="branding-outro-remove-btn">
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => outroInputRef.current?.click()}
                                                className="w-full bg-white/5 border-2 border-dashed border-white/15 hover:border-emerald-500/50 hover:bg-white/[0.07] rounded-xl py-8 px-4 flex flex-col items-center justify-center gap-2 transition-all"
                                                data-testid="branding-outro-upload-btn"
                                            >
                                                <Upload className="w-5 h-5 text-zinc-400" />
                                                <span className="text-sm text-zinc-300">Click to upload outro image</span>
                                                <span className="text-[11px] text-zinc-500">PNG / JPG · up to 5 MB</span>
                                            </button>
                                        )}
                                        <input ref={outroInputRef} type="file" accept="image/*" hidden onChange={handleOutroImageUpload} data-testid="branding-outro-file-input" />
                                    </div>
                                )}

                                {outroType === 'text' && (
                                    <div>
                                        <div className="text-sm font-semibold text-zinc-200 mb-1">Outro text</div>
                                        <p className="text-xs text-zinc-400 mb-2">Short and punchy works best — a URL, a hashtag, or a tagline.</p>
                                        <textarea
                                            value={outroText}
                                            onChange={(e) => setOutroText(e.target.value)}
                                            placeholder="e.g. Visit yourbrand.com"
                                            maxLength={120}
                                            className="w-full h-24 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-zinc-500 resize-none focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/20"
                                            data-testid="branding-outro-text-input"
                                        />
                                        <div className="text-[10px] text-zinc-500 text-right mt-1">{outroText.length}/120</div>
                                    </div>
                                )}

                                <div>
                                    <div className="text-sm font-semibold text-zinc-200 mb-1">Where on the frame?</div>
                                    <p className="text-xs text-zinc-400 mb-2">Centre is the safest pick for outro cards.</p>
                                    <PositionGrid value={outroPos} onChange={setOutroPos} testidPrefix="branding-outro" />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <div className="text-sm font-semibold text-zinc-200 mb-1">Size</div>
                                        <p className="text-xs text-zinc-400 mb-2">How much of the frame it fills.</p>
                                        <Stepper value={outroSize} onChange={setOutroSize} min={20} max={95} step={5} suffix="%" testid="branding-outro-size" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-semibold text-zinc-200 mb-1">Opacity</div>
                                        <p className="text-xs text-zinc-400 mb-2">100% = solid card.</p>
                                        <Stepper value={Math.round(outroOpacity * 100)} onChange={(v) => setOutroOpacity(v / 100)} min={10} max={100} step={5} suffix="%" testid="branding-outro-opacity" />
                                    </div>
                                </div>

                                <div className="px-3 py-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs leading-relaxed" data-testid="branding-outro-tip">
                                    <span className="font-bold">Tip:</span> the outro plays for 3 seconds at the end of your video and fades in/out automatically — no need to add the fade yourself.
                                </div>
                            </div>

                            <div className="flex justify-center md:sticky md:top-4">
                                <MockPreview aspectRatio={aspectRatio} label="Outro placement preview" testid="branding-outro-mock">
                                    {outroType === 'image' && outroImageUrl && (
                                        <OverlayMarker x={outroPos.x} y={outroPos.y} width={outroSize} opacity={outroOpacity} kind="outro">
                                            <img src={outroImageUrl} alt="" className="max-w-full max-h-full object-contain" />
                                        </OverlayMarker>
                                    )}
                                    {outroType === 'text' && (
                                        <OverlayMarker x={outroPos.x} y={outroPos.y} width={outroSize} opacity={outroOpacity} kind="outro">
                                            <span className="text-white text-xs font-semibold leading-tight whitespace-pre-wrap">
                                                {outroText.trim() || 'Your outro text'}
                                            </span>
                                        </OverlayMarker>
                                    )}
                                    {outroType === 'image' && !outroImageUrl && (
                                        <OverlayMarker x={outroPos.x} y={outroPos.y} width={outroSize} opacity={outroOpacity} kind="outro">
                                            <ImageIcon className="w-6 h-6 text-emerald-200" />
                                        </OverlayMarker>
                                    )}
                                </MockPreview>
                            </div>
                        </div>
                    )}
                </section>

                {error && (
                    <div className="mb-4 px-3 py-2 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-200 text-xs" data-testid="branding-error">
                        {error}
                    </div>
                )}

                <div className="flex items-center justify-between gap-3 pt-4 border-t border-white/10">
                    <button
                        type="button"
                        onClick={onBack}
                        className="px-4 py-2.5 rounded-xl border border-white/15 text-sm text-zinc-300 hover:bg-white/5 transition-all flex items-center gap-2"
                        data-testid="branding-back-btn"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back
                    </button>
                    <button
                        type="button"
                        onClick={handleContinue}
                        disabled={isSubmitting}
                        className="flex-1 max-w-md mx-auto px-5 py-3 rounded-xl bg-violet-600 hover:bg-violet-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm transition-all flex items-center justify-center gap-2 shadow-lg shadow-violet-600/30"
                        data-testid="branding-continue-btn"
                    >
                        {isSubmitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Uploading & saving…</>) : 'Continue to Storyboard'}
                        <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </div>
            </div>
        </div>
    );
};

export default Branding;
